import { useState } from 'react';

const PAGE_SIZES = [25, 50, 100];

/** Windowed page numbers: always the first and last page, the current page
 * and its immediate neighbours, with a "…" over any gap — matches design/
 * orion-crm-design/rendered-html/LeadsList.html's "1 2 3 … 6" pattern at
 * page 1, and stays sensible from any other page too. */
function pageWindow(current: number, totalPages: number): Array<number | '…'> {
  const pages = new Set<number>([1, totalPages, current, current - 1, current + 1].filter((p) => p >= 1 && p <= totalPages));
  const sorted = [...pages].sort((a, b) => a - b);
  const result: Array<number | '…'> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i]! - sorted[i - 1]! > 1) result.push('…');
    result.push(sorted[i]!);
  }
  return result;
}

/** The list-screen pagination footer shared across every data table
 * (design/orion-crm-design/rendered-html/LeadsList.html) — real numbered
 * pages and a real rows-per-page control, not just Previous/Next. */
export function ListPagination({
  page,
  pageSize,
  total,
  noun,
  onPage,
  onPageSize,
}: {
  page: number;
  pageSize: number;
  total: number;
  noun: string;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [pageSizeOpen, setPageSizeOpen] = useState(false);

  return (
    <div className="flex h-13 shrink-0 items-center gap-4 rounded-b-card border-t border-border bg-ground px-4 text-[13px] text-text-muted">
      <span>
        <span className="font-semibold text-ink">
          {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
        </span>{' '}
        of {total} {noun}
      </span>
      <div className="flex-1" />
      <label className="flex items-center gap-2 text-text-muted">
        Rows per page
        <div className="relative">
          <button
            type="button"
            onClick={() => setPageSizeOpen((o) => !o)}
            className="flex h-7.5 items-center gap-1 rounded-chip border border-border-strong bg-surface px-2.5 font-semibold text-ink"
          >
            {pageSize}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {pageSizeOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setPageSizeOpen(false)} />
              <div className="absolute bottom-full right-0 z-50 mb-1 w-20 overflow-hidden rounded-dialog border border-border bg-surface py-1 shadow-dialog">
                {PAGE_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => {
                      onPageSize(size);
                      setPageSizeOpen(false);
                    }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-ink hover:bg-surface-subtle"
                  >
                    {size}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </label>
      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="flex size-7 items-center justify-center rounded-[6px] text-text-secondary hover:bg-surface-subtle disabled:opacity-40"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m15 6-6 6 6 6" />
          </svg>
        </button>
        {pageWindow(page, totalPages).map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} className="w-5 text-center text-text-faint">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              aria-current={p === page ? 'page' : undefined}
              onClick={() => onPage(p)}
              className={p === page ? 'flex size-7 items-center justify-center rounded-[6px] bg-primary font-semibold text-white' : 'flex size-7 items-center justify-center rounded-[6px] text-text-secondary hover:bg-surface-subtle'}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          className="flex size-7 items-center justify-center rounded-[6px] text-text-secondary hover:bg-surface-subtle disabled:opacity-40"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
      </nav>
    </div>
  );
}
